import { z } from 'zod';
import { saldoEmMilesimos } from '../../client/domain/estoque/saldoProduto';
import { centavos } from '../../client/domain/precificacao/dinheiro';
import { milesimosDeUnidades } from '../../client/domain/precificacao/quantidade';
import { inteiroErp, numeroErp, semEnvelope } from './erpJson';

/**
 * Validação de fronteira das respostas de `GetProduto` e `GetListaProdutos`
 * (T004, Constitution IV, `contracts/erp-produto-api.md`).
 *
 * Além de validar o shape, o schema faz a conversão numérica **na fronteira**:
 * nenhum `double` de preço atravessa para dentro do domínio. Um `double` de
 * preço só existe entre a resposta HTTP e o `.transform()` abaixo
 * (`research.md`, D4).
 *
 * Mora em `src/shared/` por convenção do projeto (todos os schemas Zod ficam
 * aqui), mas importa os branded types de `src/client/domain/precificacao/` — a
 * dependência aponta só nesse sentido, e o domínio não conhece Zod.
 *
 * Os objetos são *loose* pelo mesmo motivo de `bootstrap.schema.ts`: o Checkout
 * valida o que consome e repassa o resto do payload do ERP íntegro, sem
 * reinterpretar (Constitution III).
 */

/**
 * `number/format: double` do ERP → `Centavos` inteiros.
 *
 * `numeroErp`, e não `z.number()`: o ERP real serializa todo decimal como
 * string (`"PrecoVenda1": "1.0000"`, verificado ao vivo em 2026-09-04) — ver
 * `erpJson.ts`. A conversão para centavos continua idêntica.
 */
const precoEmCentavos = numeroErp.transform((valor) => centavos(Math.round(valor * 100)));

/**
 * `PrecoMinimo` → `Centavos`, com `0` significando "sem mínimo" (017, FR-009).
 *
 * Ausente e `null` também viram `0`: o ERP só preenche o campo quando a loja
 * cadastra um mínimo, e um produto sem cadastro não pode reprovar a inserção.
 * Negativo e texto não numérico continuam sendo erro de fronteira.
 */
const precoMinimoEmCentavos = z
  .union([numeroErp.pipe(z.number().nonnegative()), z.null()])
  .default(null)
  .transform((valor) => centavos(Math.round((valor ?? 0) * 100)));

/** `QtdMinimaPreco2..5` chegam como unidades inteiras (`int64`) → `Milesimos`. */
const unidadesEmMilesimos = inteiroErp.transform((valor) => milesimosDeUnidades(valor));

/**
 * `ProdutoPesavelEditavel` restrito aos quatro valores discretos do campo
 * (AD-070). Um quinto valor é **erro de fronteira**, não um comportamento
 * silencioso a mais.
 */
export const pesavelEditavelSchema = z.enum(['S', 'B', '', 'E']);

/** `SDTCheckout_GetProduto` — a única origem possível de `SnapshotPrecoProduto`. */
export const sdtCheckoutGetProdutoSchema = z.looseObject({
  CodigoProduto: z.string(),
  Descricao: z.string(),
  UDM: z.string(),
  /** Preço já resolvido pelo ERP — usado para todo `TipoPreco ≠ 8` (AD-059/AD-060). */
  PrecoVenda: precoEmCentavos,
  PrecoVenda1: precoEmCentavos,
  PrecoVenda2: precoEmCentavos,
  PrecoVenda3: precoEmCentavos,
  PrecoVenda4: precoEmCentavos,
  PrecoVenda5: precoEmCentavos,
  /**
   * Preço mínimo do produto: limita o desconto do item `'E'` (017, US2). Até a
   * 017 atravessava o `looseObject` sem ser lido, embora o T004 da 003 pedisse
   * a conversão.
   */
  PrecoMinimo: precoMinimoEmCentavos,
  QtdMinimaPreco2: unidadesEmMilesimos,
  QtdMinimaPreco3: unidadesEmMilesimos,
  QtdMinimaPreco4: unidadesEmMilesimos,
  QtdMinimaPreco5: unidadesEmMilesimos,
  ProdutoPesavelEditavel: pesavelEditavelSchema,
  /**
   * Saldo de estoque do produto (contrato de 2026-09-14, AD-236), convertido em
   * milésimos **com sinal** na fronteira — o ERP devolve `"78.000"` e também
   * `"-205.000"`.
   *
   * `optional()`: o ERP anterior a esse contrato não publica o campo, e a
   * ausência significa "saldo desconhecido", que não valida nada. O ERP
   * preenche `0` quando a empresa não controla saldo (`EmpSldPro` fora de
   * `'A'`/`'B'`), caso em que a política da sessão já é `''`.
   *
   * Não vai para `SnapshotPrecoProduto`: o snapshot é congelado na linha e no
   * cache da venda, e o saldo precisa ser reconsultado a cada decisão.
   */
  Saldo: numeroErp.transform((valor) => saldoEmMilesimos(valor)).optional(),
  /**
   * Se o produto tem cenário tributário para a venda (AD-258) — calculado pelo
   * ERP a partir do cliente (`Codcliente`). Booleano **nativo** no JSON, medido em
   * 2026-10-01; com `false` o ERP acrescenta `messages` (`Type: 1`) e a
   * resposta ganha o envelope `Produto` (regra de AD-218, que `semEnvelope`
   * absorve).
   *
   * `optional()`: um ERP anterior ao campo não o publica, e a ausência vale
   * "válido" — é o comportamento de antes, em que o cenário só era checado no
   * `FaturarNFCe`. Recusar por ausência travaria toda inserção.
   */
  CenarioValido: z.boolean().optional(),
});

/**
 * `GET /ApiCentriumOAuth/GetProduto`.
 *
 * **Devolve o SDT do produto, não um envelope.** O `GetProdutoOutput` do YAML
 * desenha `{"Produto": …}`, mas o ERP real entrega os campos na raiz da
 * resposta (verificado ao vivo em 2026-09-04 — AD-165). `semEnvelope` aceita as
 * duas formas e sempre entrega o objeto interno, então quem consome lê o
 * produto direto do `safeParse`.
 */
export const getProdutoOutputSchema = semEnvelope('Produto', sdtCheckoutGetProdutoSchema);

/**
 * Candidato do modal de busca.
 *
 * Só os campos que a lista pode legitimamente fornecer: exibir e escolher. Não
 * há `PrecoVenda` nem `ProdutoPesavelEditavel` neste schema porque o ERP não os
 * devolve aqui — montar uma `LinhaCarrinho` a partir da busca é proibido
 * (AD-091, `research.md` D1).
 */
export const produtoDaListaSchema = z.looseObject({
  CodigoProduto: z.string(),
  Descricao: z.string(),
  Referencia: z.string(),
  CodigoBarras: z.string(),
  UDM: z.string(),
  /**
   * Saldo de estoque (`"-1.000"`, `"78.000"`), em milésimos com sinal — exibido
   * na coluna "Saldo" e filtrado localmente pelo modal (AD-258). Não decide
   * inserção: quem decide é o `Saldo` fresco de `GetProduto` (AD-236).
   *
   * `optional()` pelo mesmo motivo de `Saldo` acima.
   */
  Estoque: numeroErp.transform((valor) => saldoEmMilesimos(valor)).optional(),
});

export const checkoutListaProdutosSchema = z.looseObject({
  PaginaAtual: inteiroErp,
  RegistrosPorPagina: inteiroErp,
  TotalRegistros: inteiroErp,
  TotalPaginas: inteiroErp,
  /** Ausente na busca sem resultado — ver `Clientes` em `cliente.schema.ts`. */
  Produtos: z.array(produtoDaListaSchema).optional().default([]),
});

/**
 * `GET /ApiCentriumOAuth/GetListaProdutos` — também **sem** envelope no ERP
 * real: a resposta traz `Produtos`/`PaginaAtual`/`TotalRegistros` na raiz, sem
 * a chave `ListaProdutos` do YAML (AD-165).
 */
export const getListaProdutosOutputSchema = semEnvelope(
  'ListaProdutos',
  checkoutListaProdutosSchema,
);

export type SdtCheckoutGetProduto = z.infer<typeof sdtCheckoutGetProdutoSchema>;
export type ProdutoDaLista = z.infer<typeof produtoDaListaSchema>;
export type CheckoutListaProdutos = z.infer<typeof checkoutListaProdutosSchema>;
